import { useToast } from 'primevue/usetoast';
import type { ToastMessageOptions } from 'primevue/toast';

// Global toast instance that will be set from a component
let globalToastInstance: ReturnType<typeof useToast> | null = null;

// Function to set the global toast instance (called from App.vue or main layout)
export const setGlobalToast = (toastInstance: ReturnType<typeof useToast>) => {
  globalToastInstance = toastInstance;
};

// Get the toast instance
export const getToast = () => {
  if (!globalToastInstance) {
    // Try to get it from useToast if we're in a component context
    try {
      globalToastInstance = useToast();
    } catch (error) {
      throw new Error('No PrimeVue Toast provided! Make sure Toast component is mounted and setGlobalToast is called.');
    }
  }
  return globalToastInstance;
};

// Extended toast interface that includes dismiss and more advanced features
interface ExtendedToastOptions extends ToastMessageOptions {
  duration?: number;
  retryCallback?: () => Promise<void>;
}

interface ToastMessage {
  id: string;
  options: ExtendedToastOptions;
}

// Track active messages for dismissal
const activeMessages: Map<string, ToastMessage> = new Map();

// Enhanced toast utility with advanced features
export const toast = {
  success: (message: string, summary?: string, options?: Partial<ExtendedToastOptions>) => {
    const toastService = getToast();
    const id = Math.random().toString(36).substr(2, 9);
    
    const toastOptions: ToastMessageOptions = {
      severity: 'success',
      summary: summary || 'Success',
      detail: message,
      life: options?.duration || 5000,
      ...options
    };
    
    activeMessages.set(id, { id, options: { ...toastOptions, ...options } });
    toastService.add(toastOptions);
    
    return { id, dismiss: () => toast.dismiss(id) };
  },
  
  error: (message: string | { message: string; duration?: number }, summary?: string, options?: Partial<ExtendedToastOptions>) => {
    const toastService = getToast();
    const id = Math.random().toString(36).substr(2, 9);
    
    // Handle both string and object format for compatibility with notyf
    const messageText = typeof message === 'string' ? message : message.message;
    const duration = typeof message === 'object' && message.duration !== undefined ? message.duration : (options?.duration || 7000);
    
    const toastOptions: ToastMessageOptions = {
      severity: 'error',
      summary: summary || 'Error',
      detail: messageText,
      life: duration === 0 ? 0 : duration, // 0 means no auto-dismiss
      ...options
    };
    
    activeMessages.set(id, { id, options: { ...toastOptions, ...options } });
    toastService.add(toastOptions);
    
    return { id, dismiss: () => toast.dismiss(id) };
  },
  
  warning: (message: string, summary?: string, options?: Partial<ExtendedToastOptions>) => {
    const toastService = getToast();
    const id = Math.random().toString(36).substr(2, 9);
    
    const toastOptions: ToastMessageOptions = {
      severity: 'warn',
      summary: summary || 'Warning',
      detail: message,
      life: options?.duration || 6000,
      ...options
    };
    
    activeMessages.set(id, { id, options: { ...toastOptions, ...options } });
    toastService.add(toastOptions);
    
    return { id, dismiss: () => toast.dismiss(id) };
  },
  
  info: (message: string, summary?: string, options?: Partial<ExtendedToastOptions>) => {
    const toastService = getToast();
    const id = Math.random().toString(36).substr(2, 9);
    
    const toastOptions: ToastMessageOptions = {
      severity: 'info',
      summary: summary || 'Information',
      detail: message,
      life: options?.duration || 5000,
      ...options
    };
    
    activeMessages.set(id, { id, options: { ...toastOptions, ...options } });
    toastService.add(toastOptions);
    
    return { id, dismiss: () => toast.dismiss(id) };
  },

  // Generic open method for compatibility with notyf
  open: (options: { type: string; message: string; duration?: number }) => {
    const { type, message, duration } = options;
    const id = Math.random().toString(36).substr(2, 9);
    
    let severity: 'success' | 'info' | 'warn' | 'error' = 'info';
    let summary = 'Notification';
    
    switch (type) {
      case 'success':
        severity = 'success';
        summary = 'Success';
        break;
      case 'error':
        severity = 'error';
        summary = 'Error';
        break;
      case 'warning':
        severity = 'warn';
        summary = 'Warning';
        break;
      case 'info':
        severity = 'info';
        summary = 'Information';
        break;
    }
    
    const toastOptions: ToastMessageOptions = {
      severity,
      summary,
      detail: message,
      life: duration || 5000
    };
    
    const toastService = getToast();
    activeMessages.set(id, { id, options: toastOptions });
    toastService.add(toastOptions);
    
    return { id, dismiss: () => toast.dismiss(id) };
  },

  // Dismiss a specific message
  dismiss: (messageId: string) => {
    const toastService = getToast();
    // PrimeVue Toast doesn't have direct dismiss by ID, so we'll clear all for now
    // This is a limitation we need to work around
    toastService.removeAllGroups();
    activeMessages.delete(messageId);
  },

  // Clear all messages
  clear: () => {
    const toastService = getToast();
    toastService.removeAllGroups();
    activeMessages.clear();
  }
};

// Legacy compatibility - export the same structure as the old notyf
export const notify = toast;

// Create an object that mimics the notyf API for full compatibility
export const notyf = {
  success: toast.success,
  error: toast.error,
  open: toast.open,
  dismiss: toast.dismiss
};

// Export the raw toast service for advanced usage
export { getToast as useToastService };
